/**
 * Container: hooks, search/filter state, fetch logic, navigation and download handlers.
 */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Stack } from "@fluentui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { APP_ROUTES, getCheckInDocumentsNavLabel, NAV_LOCATION_STATE } from "../../routes/routeConfig";
import { useTheme } from "../../theme/OAS365Theme";
import { useGridStyle } from "../../theme/styles/GridStyle";
import { useLogin } from "../../redux/hooks/useLogin";
import { useOneDriveDocsList } from "../../redux/hooks/useOneDriveDocsList";
import { useOneDrive } from "../../redux/hooks/useOneDrive";
import { usePreviewInTeams } from "../../redux/hooks/usePreviewInTeams";
import { useOpenInIFS } from "../../redux/hooks/useOpenInIFS";
import Loader from "../../components/LoadSpinner";
import DocumentsPagination from "../document-tabs/components/all-documents/DocumentsPagination";
import { SEARCH_RESULT_FAILED } from "../../redux/constants/messageTypes";
import {
  buildFetchParams,
  mapDocsToItems,
  resolveGroupId,
  filterDocsForDisplay,
  resolveEffectiveSearchValue,
  isOneDriveFileTypeFilterActive,
  resolveOneDriveListFetchPaging,
  paginateOneDriveDocs,
  collectCheckInDocumentsFromSelection,
  normalizeBrowseFiles,
  setBrowseSupportedExtensionsFromApi,
  getBrowseAcceptAttribute,
  getBrowseAcceptExtensions,
} from "./checkInNewList.utils";
import TeamsSearchBar from "./components/TeamsSearchBar";
import { getDefaultDateRange } from "./components/DateRangeFilter";
import { CheckInNewDocumentsTable } from "./components/CheckInNewDocumentsTable";
import { CheckInNewFolderBreadcrumb } from "./components/CheckInNewFolderBreadcrumb";
import { CheckInNewListNoDataDisplay } from "./components/CheckInNewListNoDataDisplay";
import { CheckInNewListErrorDisplay } from "./components/CheckInNewListErrorDisplay";
import {
  DELETE_DOCUMENT_TITLE,
  getDeleteDocumentConfirmation,
  DELETE_DOCUMENT_SUB_TITLE,
} from "../../redux/constants/messageTypes";
import ReusableMessageDialog from "../../components/popupModals/ReusableMessageDialog";
import ConfirmModal from "../../components/popupModals/ConfirmModal";
import ShareDocumentDialog from "./components/ShareDocumentDialog";
import { SharingDetailsDialog } from "./components/SharingDetailsDialog";
import { OneDriveVersionHistoryDialog } from "./components/OneDriveVersionHistoryDialog";
import { HoverSubmenuMenuItem } from "../../components/common/HoverSubmenuMenuItem";
import { logger } from "../../utils/logger";
import { buildOneDriveBulkDownloadZipFilename } from "../../utils/downloadFilenames";
import { openExternalUrl } from "../../utils/openExternalUrl";
import { resolveEditInBrowserUrl } from "../../utils/resolveEditInBrowserUrl";
import {
  createOneDriveShareLink,
  isNewSharingLink,
} from "../../utils/createOneDriveShareLink";
import { markOneDriveDocumentShared } from "../../redux/slices/oneDrive";
import { copyTextToClipboard } from "../../utils/copyTextToClipboard";
import { getTitle, getFileIconPath } from "../../functions/helperMethod";
import UserAuth from "../../auth/userAuth";
import { useAppDispatch } from "../../redux/hooks";
import { getFileTypeInfo, postOneDriveBulkDownloadZip, getOneDriveVersionHistory } from "../../redux/api/documentsAPI";
import {
  ON_SUCCESS_MESSAGE_INVOKE,
  ON_ERROR_MESSAGE_INVOKE,
} from "../../redux/constants/actionTypes";
import { ERROR_TYPE, SUCCESS_TYPE, DELETE_FAILED_MESSAGE } from "../../redux/constants/messageTypes";
import { DOC_CHECK_IN_TYPE, DOCUMENT_MODE } from "../../redux/constants/metaData";

const PAGE_SIZE = 20;
/** Matches `OneDriveBulkDownloadMaxFiles` on the API. */
const MAX_BULK_ONEDRIVE_ZIP_FILES = 50;
const NO_DOCS_IN_FOLDER_MESSAGE =
  "No documents found in the selected folder(s).";

function readRestoreFolderState(locationState) {
  const restore = locationState?.[NAV_LOCATION_STATE.restoreCheckInNewFolder];
  if (!restore) {
    return { folderStack: [], pageIndex: 0 };
  }
  return {
    folderStack: Array.isArray(restore.folderStack) ? restore.folderStack : [],
    pageIndex:
      typeof restore.pageIndex === "number" && restore.pageIndex >= 0
        ? restore.pageIndex
        : 0,
  };
}

export default function CheckInNewListContent({ isPanelActive = true }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const gridThemeStyle = useGridStyle();
  const { docs, loading, isError, message, fetchOneDriveDocs, refreshTrigger, initialized } = useOneDriveDocsList();
  const { downloadOneDriveToLocal, downloadState, closeDownloadDialog, deleteOneDriveDocument, openSharingDetails, closeSharingDetails, fetchSharingDetails, stopSharing, removeSharingLink, updateSharingRole, sharingDetailsState } = useOneDrive();
  const { openInIFS } = useOpenInIFS();
  const downloadIsOpen = downloadState?.isOpen ?? false;
  const downloadLoading = downloadState?.loading ?? false;
  const downloadIsError = downloadState?.isError ?? false;
  const downloadMessage = downloadState?.message ?? "";
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [checkInResolving, setCheckInResolving] = useState(false);
  const [checkInResolveError, setCheckInResolveError] = useState(null);
  const showDownloadDialog =
    downloadLoading ||
    bulkDownloading ||
    (downloadIsOpen && downloadIsError) || bulkDownloading;

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [stopSharingConfirmOpen, setStopSharingConfirmOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [versionHistoryData, setVersionHistoryData] = useState([]);
  const [versionHistoryLoading, setVersionHistoryLoading] = useState(false);
  const [versionHistoryError, setVersionHistoryError] = useState(null);
  const [versionHistoryDocument, setVersionHistoryDocument] = useState(null);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [itemToShare, setItemToShare] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [shareBusyLabel, setShareBusyLabel] = useState("");
  const [shareError, setShareError] = useState("");

  const handleDownloadDialogClose = useCallback(() => {
    if (downloadLoading || bulkDownloading) {
      return;
    }
    closeDownloadDialog();
  }, [bulkDownloading, closeDownloadDialog, downloadLoading]);
  const hasShownDownloadToast = useRef(false);
  const lastDownloadedDocumentTitleRef = useRef("Document");

  const { teamsContext } = useLogin();
  const isGeneralAppContext = !teamsContext?.channel?.id;
  const {
    previewInTeams,
    state: previewState,
    closePreviewDialog,
  } = usePreviewInTeams();
  const previewIsOpen = previewState?.isOpen ?? false;
  const previewIsError = previewState?.isError ?? false;
  const previewMessage = previewState?.message ?? "";
  const previewLoading = previewState?.loading ?? false;
  const previewLoadingMessage = previewState?.loadingMessage ?? "";
  const previewErrorMessage = typeof previewState?.error?.message === "string"
    ? previewState.error.message
    : (previewState?.error ? String(previewState.error) : "");

  const [searchValue, setSearchValue] = useState("");
  const [appliedSearchValue, setAppliedSearchValue] = useState("");
  const [isOneDriveFilterVisible, setIsOneDriveFilterVisible] = useState(false);
  const [oneDriveSelectedFileType, setOneDriveSelectedFileType] = useState("");
  const [dateRange, setDateRange] = useState(() => getDefaultDateRange());
  const [appliedDateRange, setAppliedDateRange] = useState(null);
  const [isDateFilterEnabled, setIsDateFilterEnabled] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(
    () => readRestoreFolderState(location.state).pageIndex
  );
  /** @type {[Array<{ id: string, name: string }>, Function]} */
  const [folderStack, setFolderStack] = useState(
    () => readRestoreFolderState(location.state).folderStack
  );
  const lastListScopeKeyRef = useRef("");
  const isFirstScopeSyncRef = useRef(true);
  const clientRequestIdRef = useRef(0);
  const lastHandledRefreshTriggerRef = useRef(0);
  const isFirstRefreshTriggerSyncRef = useRef(true);
  const fileTypesFetchedRef = useRef(false);
  const fileTypesFetchStartedRef = useRef(false);
  const [fileTypesReady, setFileTypesReady] = useState(false);
  /** Source of truth for cross-folder multi-select. */
  const [selectedByKey, setSelectedByKey] = useState({});
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const selectedByKeyRef = useRef(selectedByKey);
  selectedByKeyRef.current = selectedByKey;
  const selectedCount = useMemo(() => Object.keys(selectedByKey).length, [selectedByKey]);
  const tableItemsRef = useRef([]);

  const currentFolderId =
    folderStack.length > 0 ? folderStack[folderStack.length - 1].id : "";
  const channelDocsLabel = getCheckInDocumentsNavLabel(teamsContext);
  const { groupId: scopeGroupId, channelId: scopeChannelId } =
    resolveGroupId(teamsContext);
  const listScopeKey = `${teamsContext?.user?.tenant?.id ?? ""}|${scopeGroupId ?? ""}|${scopeChannelId ?? ""}`;

  const dispatchOneDriveFetch = useCallback(
    (params) => {
      clientRequestIdRef.current += 1;
      fetchOneDriveDocs({
        ...params,
        clientRequestId: clientRequestIdRef.current,
      });
    },
    [fetchOneDriveDocs],
  );

  const handleToggleRowSelection = useCallback((item, checked) => {
    const key = String(item?.key ?? "");
    if (!key) return;

    setSelectedByKey((prev) => {
      const next = { ...prev };
      if (checked) {
        next[key] = item;
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  /** Opening ... on an unselected row selects only that row; already-selected keeps multi-select. */
  const handleSelectRowForMenu = useCallback((item) => {
    const key = String(item?.key ?? "");
    if (!key) return;

    setSelectedByKey((prev) => {
      if (prev[key]) {
        return prev;
      }
      return { [key]: item };
    });
  }, []);

  const handleToggleAllVisible = useCallback((checked) => {
    setSelectedByKey((prev) => {
      const next = { ...prev };
      tableItemsRef.current.forEach((item) => {
        const key = String(item?.key ?? "");
        if (!key) return;
        if (checked) {
          next[key] = item;
        } else {
          delete next[key];
        }
      });
      return next;
    });
  }, []);

  const clearStoredSelection = useCallback(() => {
    setSelectedByKey({});
    setShowSelectedOnly(false);
  }, []);

  const getAllSelectedItems = useCallback(
    () => Object.values(selectedByKeyRef.current),
    []
  );

  const activeDateRange = useMemo(
    () => (isDateFilterEnabled ? appliedDateRange : null),
    [isDateFilterEnabled, appliedDateRange],
  );

  const fetchParams = useMemo(
    () =>
      buildFetchParams({
        teamsContext,
        searchValue: appliedSearchValue,
        oneDriveSelectedFileType,
        pageSize: PAGE_SIZE,
        folderId: currentFolderId,
        dateRange: activeDateRange,
      }),
    [teamsContext, appliedSearchValue, oneDriveSelectedFileType, currentFolderId, activeDateRange]
  );

  const useClientSideFileTypePaging = isOneDriveFileTypeFilterActive(
    oneDriveSelectedFileType,
  );

  const fetchDocsAtFolder = useCallback(
    (folderId, pageIndex = 0, overrides = {}) => {
      const effectiveFileType =
        overrides.oneDriveSelectedFileType !== undefined
          ? overrides.oneDriveSelectedFileType
          : oneDriveSelectedFileType;
      const effectiveDateRange =
        overrides.dateRange !== undefined ? overrides.dateRange : activeDateRange;
      const paging = resolveOneDriveListFetchPaging({
        oneDriveFileType: effectiveFileType,
        pageIndex,
        pageSize: PAGE_SIZE,
      });
      dispatchOneDriveFetch({
        ...buildFetchParams({
          teamsContext,
          searchValue:
            overrides.searchValue !== undefined
              ? overrides.searchValue
              : appliedSearchValue,
          oneDriveSelectedFileType: effectiveFileType,
          pageSize: PAGE_SIZE,
          folderId,
          dateRange: effectiveDateRange,
        }),
        recFrom: paging.recFrom,
        pageSize: paging.recSize,
      });
    },
    [
      dispatchOneDriveFetch,
      teamsContext,
      appliedSearchValue,
      oneDriveSelectedFileType,
      activeDateRange,
    ]
  );

  const filteredDocs = useMemo(
    () =>
      useClientSideFileTypePaging
        ? filterDocsForDisplay(docs ?? [], {
            oneDriveFileType: oneDriveSelectedFileType,
          })
        : docs ?? [],
    [docs, oneDriveSelectedFileType, useClientSideFileTypePaging]
  );

  const pagedDocs = useMemo(
    () =>
      useClientSideFileTypePaging
        ? paginateOneDriveDocs(filteredDocs, currentPageIndex, PAGE_SIZE)
        : filteredDocs,
    [filteredDocs, currentPageIndex, useClientSideFileTypePaging]
  );

  const tableItems = useMemo(() => mapDocsToItems(pagedDocs), [pagedDocs]);
  const allSelectedItems = useMemo(
    () => Object.values(selectedByKey),
    [selectedByKey]
  );
  const displayTableItems = useMemo(() => {
    if (!showSelectedOnly) {
      return tableItems;
    }
    const start = currentPageIndex * PAGE_SIZE;
    return allSelectedItems.slice(start, start + PAGE_SIZE);
  }, [showSelectedOnly, tableItems, allSelectedItems, currentPageIndex]);
  tableItemsRef.current = displayTableItems;

  // Fetch a specific page from the server
  const fetchPage = useCallback(
    (pageIndex) => {
      fetchDocsAtFolder(currentFolderId, pageIndex);
    },
    [fetchDocsAtFolder, currentFolderId]
  );

  const handlePageChange = useCallback(
    (pageIndex) => {
      setCurrentPageIndex(pageIndex);
      if (!showSelectedOnly && !useClientSideFileTypePaging) {
        fetchPage(pageIndex);
      }
    },
    [fetchPage, showSelectedOnly, useClientSideFileTypePaging]
  );

  useEffect(() => {
    if (selectedCount === 0) {
      setShowSelectedOnly(false);
    }
  }, [selectedCount]);

  useEffect(() => {
    if (!isPanelActive) return;
    if (fileTypesFetchedRef.current || fileTypesFetchStartedRef.current) return;

    fileTypesFetchStartedRef.current = true;
    (async () => {
      try {
        const { data } = await getFileTypeInfo();
        if (setBrowseSupportedExtensionsFromApi(data)) {
          fileTypesFetchedRef.current = true;
          setFileTypesReady(true);
        }
      } catch (error) {
        logger.warn("getFileTypeInfo failed", error, "UI");
      }
    })();
  }, [isPanelActive]);

  // Initial fetch when tenant/channel scope changes; skip when accordion reopens with cached list.
  useEffect(() => {
    if (!teamsContext?.user?.tenant?.id || !isPanelActive) return;
    const skipRouteRefresh =
      location.state?.[NAV_LOCATION_STATE.skipDocumentPanelRouteRefresh] === true;

    if (isFirstScopeSyncRef.current) {
      isFirstScopeSyncRef.current = false;
      lastListScopeKeyRef.current = listScopeKey;
      if (initialized || skipRouteRefresh) return;
    }

    const scopeChanged = lastListScopeKeyRef.current !== listScopeKey;
    if (!scopeChanged && initialized) return;

    lastListScopeKeyRef.current = listScopeKey;
    clearStoredSelection();
    setFolderStack([]);
    setCurrentPageIndex(0);
    fetchDocsAtFolder("", 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scope key covers tenant + channel; filter/search refetch separately
  }, [listScopeKey, isPanelActive, location.state, clearStoredSelection]);

  // Refetch only when refreshTrigger increments, not when the accordion reopens.
  useEffect(() => {
    if (!teamsContext?.user?.tenant?.id || !isPanelActive) return;
    const trigger = refreshTrigger ?? 0;
    const skipRouteRefresh =
      location.state?.[NAV_LOCATION_STATE.skipDocumentPanelRouteRefresh] === true;
    if (isFirstRefreshTriggerSyncRef.current) {
      isFirstRefreshTriggerSyncRef.current = false;
      if (skipRouteRefresh) {
        lastHandledRefreshTriggerRef.current = trigger;
        return;
      }
      // If a refresh was already requested before this panel mounted (e.g. after a
      // successful check-in), honour it instead of dropping the pending update.
      if (trigger === 0) {
        lastHandledRefreshTriggerRef.current = trigger;
        return;
      }
    }
    if (trigger === 0 || trigger === lastHandledRefreshTriggerRef.current) return;
    lastHandledRefreshTriggerRef.current = trigger;
    clearStoredSelection();
    setFolderStack([]);
    setCurrentPageIndex(0);
    fetchDocsAtFolder("", 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshTrigger increments only
  }, [refreshTrigger, teamsContext?.user?.tenant?.id, isPanelActive, location.state, clearStoredSelection]);

  useEffect(() => {
    const isDownloadSuccess = downloadIsOpen && !downloadLoading && !downloadIsError;
    if (isDownloadSuccess && !hasShownDownloadToast.current) {
      hasShownDownloadToast.current = true;
      const documentTitle = lastDownloadedDocumentTitleRef.current || "Document";
      dispatch({
        type: ON_SUCCESS_MESSAGE_INVOKE,
        payload: {
          type: SUCCESS_TYPE,
          title: "Download completed",
          message: `${documentTitle} downloaded to local successfully!`,
        },
      });
    }

    if (!downloadIsOpen) {
      hasShownDownloadToast.current = false;
    }
  }, [downloadIsOpen, downloadLoading, downloadIsError, dispatch]);

  const handleDownload = useCallback(
    (documentOrId, isLocal = false) => {
      const documentId =
        typeof documentOrId === "object" && documentOrId !== null
          ? documentOrId.key
          : documentOrId;
      const documentTitle =
        typeof documentOrId === "object" && documentOrId !== null
          ? documentOrId.title || documentOrId.name || "Document"
          : "Document";

      lastDownloadedDocumentTitleRef.current = documentTitle;

      const { groupId, channelId, fileRoot } = resolveGroupId(teamsContext);
      const tenantId = teamsContext?.user?.tenant?.id ?? "";
      if (!tenantId) {
        logger.warn("[CheckInNewListContent] handleDownload skipped: no tenantId", null, "DIALOG");
        return;
      }
      downloadOneDriveToLocal(groupId, channelId, tenantId, documentId, fileRoot, undefined, isLocal);
    },
    [teamsContext, downloadOneDriveToLocal]
  );

  const handleDownloadSelectedToLocal = useCallback(async () => {
    const selectedRows = getAllSelectedItems().filter(
      (row) => row?.isFolder !== true
    );
    if (!selectedRows?.length || bulkDownloading) {
      return;
    }

    const { groupId, channelId, fileRoot } = resolveGroupId(teamsContext);
    const tenantId = teamsContext?.user?.tenant?.id ?? "";
    if (!tenantId) {
      logger.warn(
        "[CheckInNewListContent] bulk download skipped: no tenantId",
        null,
        "DIALOG"
      );
      return;
    }

    if (selectedRows.length === 1) {
      handleDownload(selectedRows[0], true);
      return;
    }

    const documentIds = selectedRows
      .map((row) => row?.key)
      .filter((id) => id != null && id !== "");

    if (!documentIds.length) {
      return;
    }

    if (documentIds.length > MAX_BULK_ONEDRIVE_ZIP_FILES) {
      dispatch({
        type: ON_ERROR_MESSAGE_INVOKE,
        payload: {
          type: ERROR_TYPE,
          message: `You can download at most ${MAX_BULK_ONEDRIVE_ZIP_FILES} documents in one ZIP. Select fewer rows or download in batches.`,
        },
      });
      return;
    }

    setBulkDownloading(true);
    try {
      const userAuth = new UserAuth();
      const tokenResponse = await userAuth.getUserToken(true);
      const accessToken = tokenResponse?.accessToken ?? null;
      if (!accessToken) {
        dispatch({
          type: ON_ERROR_MESSAGE_INVOKE,
          payload: {
            type: ERROR_TYPE,
            message: "No access token. Sign in again and retry.",
          },
        });
        return;
      }

      const res = await postOneDriveBulkDownloadZip(accessToken, {
        tenantId,
        teamId: groupId,
        channelId,
        oneDriveRoot: fileRoot ?? "",
        documentIds,
      });

      const blob =
        res.data instanceof Blob ? res.data : new Blob([res.data], { type: "application/zip" });
      const filename = buildOneDriveBulkDownloadZipFilename();

      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      anchor.style.display = "none";
      document.body.appendChild(anchor);

      /*
       Close the modal before triggering the synthetic click so Chromium/Edge
       attaches the ZIP to native Downloads like a normal save. Revoking the
       object URL synchronously breaks in-flight downloads (often shows Removed).
       */
      setBulkDownloading(false);
      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });

      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 120_000);

      dispatch({
        type: ON_SUCCESS_MESSAGE_INVOKE,
        payload: {
          type: SUCCESS_TYPE,
          title: "Download completed",
          message: `${documentIds.length} document(s) saved as one ZIP file.`,
        },
      });
    } catch (err) {
      let msg = "Bulk download failed.";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const t = await data.text();
          if (t && t.trim()) msg = t.trim();
        } catch {
          /* keep default */
        }
      } else if (typeof data?.message === "string" && data.message) {
        msg = data.message;
      } else if (err?.message) {
        msg = err.message;
      }
      dispatch({
        type: ON_ERROR_MESSAGE_INVOKE,
        payload: {
          type: ERROR_TYPE,
          message: msg,
        },
      });
      logger.warn(
        "[CheckInNewListContent] bulk ZIP download failed",
        { msg, status: err?.response?.status },
        "DIALOG",
      );
    } finally {
      setBulkDownloading(false);
    }
  }, [getAllSelectedItems, bulkDownloading, teamsContext, dispatch, handleDownload]);

  const handleCheckIn = useCallback(
    (item) => {
      const selectedRevisionItem =
        item?.docClass && item?.docNo && item?.docRev
          ? {
              class: item.docClass,
              docNo: item.docNo,
              docRev: item.docRev,
              sheetNo: item.docSheet ?? "",
              title: item?.name?.substring(0, item?.name?.indexOf(".")) ?? "",
              format: item.formatSize ?? "",
            }
          : null;

      navigate(APP_ROUTES.checkIn, {
        state: item
          ? {
              selectedDocument: item,
              selectedRevisionItem,
              docTitle:
                item?.name?.substring(0, item?.name?.indexOf(".")) ?? "",
              selectedDocumentDownloadUrl: item?.downloadlink ?? null,
              initialTab: "newDocument",
              /** So New Revision tab can reset IFS Document * when user switches from New Document. */
              from: APP_ROUTES.checkInNew,
            }
          : undefined,
      });
    },
    [navigate]
  );

  const handleBrowseSelections = useCallback(
    async (files) => {
      const normalized = await normalizeBrowseFiles(files);
      if (!normalized.length) {
        dispatch({
          type: ON_ERROR_MESSAGE_INVOKE,
          payload: {
            type: ERROR_TYPE,
            message: "No supported documents were selected.",
          },
        });
        return;
      }

      const mapped = normalized.map((file) => {
        const ext = file.name?.includes(".")
          ? file.name.split(".").pop()
          : "";
        const iconName = getFileIconPath(file.type || "", { FileExtension: ext });
        return {
          key: file.id,
          id: file.id,
          name: file.name,
          title: file.name,
          iconName,
          downloadlink: null,
          fileType: file.type || "",
          weblink: null,
          webLinkUrl: null,
          documentMode: DOCUMENT_MODE.oneDrive,
          source: file.source,
          base64str: file.base64str,
          file: file.file,
        };
      });

      if (mapped.length === 1) {
        const single = mapped[0];
        navigate(APP_ROUTES.checkIn, {
          state: {
            selectedDocument: {
              ...single,
              key: single.id,
              name: single.name,
              title: single.name,
              fileType: single.fileType,
              downloadlink: null,
            },
            docTitle: single.name?.substring(0, single.name?.indexOf(".")) ?? "",
            selectedDocumentDownloadUrl: null,
            initialTab: "newDocument",
            from: APP_ROUTES.checkInNew,
          },
        });
        return;
      }

      navigate(APP_ROUTES.checkInNewBulk, {
        state: {
          documents: mapped,
          from: APP_ROUTES.checkInNew,
        },
      });
    },
    [dispatch, navigate],
  );

  const openFileFallbackPicker = useCallback(async () => {
    return new Promise((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = true;
      input.accept = getBrowseAcceptAttribute();
      input.onchange = () => resolve(Array.from(input.files ?? []));
      input.oncancel = () => resolve([]);
      input.click();
    });
  }, []);

  // Folder fallback: webkitdirectory lets the user select a whole folder.
  // The browser shows an "Upload to this site?" confirmation — that is a
  // browser security requirement and cannot be suppressed. After the user
  // clicks Upload, onchange fires and the selected files are processed.
  const openFolderFallbackPicker = useCallback(async () => {
    return new Promise((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.webkitdirectory = true;
      input.multiple = true;
      input.onchange = () => resolve(Array.from(input.files ?? []));
      input.oncancel = () => resolve([]);
      input.click();
    });
  }, []);

  const handleBrowseFiles = useCallback(async () => {
    if (typeof window !== "undefined" && typeof window.showOpenFilePicker === "function") {
      try {
        const handles = await window.showOpenFilePicker({
          multiple: true,
          types: [
            {
              description: "Supported files",
              accept: {
                "application/*": getBrowseAcceptExtensions(),
              },
            },
          ],
        });
        const files = await Promise.all(handles.map((h) => h.getFile()));
        if (!files.length) return;
        await handleBrowseSelections(files);
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
        logger.warn("showOpenFilePicker failed, falling back to input picker", error, "DIALOG");
      }
    }
    const files = await openFileFallbackPicker();
    if (!files.length) return;
    await handleBrowseSelections(files);
  }, [handleBrowseSelections, openFileFallbackPicker]);

  const handleBrowseFolder = useCallback(async () => {
    if (typeof window !== "undefined" && typeof window.showDirectoryPicker === "function") {
      try {
        const handle = await window.showDirectoryPicker();
        const entries = [];
        const stack = [handle];
        while (stack.length) {
          const current = stack.pop();
          for await (const entry of current.values()) {
            if (entry.kind === "directory") {
              stack.push(entry);
            } else if (entry.kind === "file") {
              entries.push(entry);
            }
          }
        }
        const files = await Promise.all(entries.map((entry) => entry.getFile()));
        const supported = files.filter(Boolean);
        if (!supported.length) {
          dispatch({
            type: ON_ERROR_MESSAGE_INVOKE,
            payload: { type: ERROR_TYPE, message: "No supported documents found in selected folder." },
          });
          return;
        }
        await handleBrowseSelections(supported);
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
        logger.warn("showDirectoryPicker failed, falling back to input picker", error, "DIALOG");
      }
    }
    const files = await openFolderFallbackPicker();
    if (!files.length) return;
    await handleBrowseSelections(files);
  }, [dispatch, handleBrowseSelections, openFolderFallbackPicker]);

  const navigateToCheckInForDocuments = useCallback(
    (rows) => {
      if (!rows?.length) {
        setCheckInResolveError(NO_DOCS_IN_FOLDER_MESSAGE);
        return;
      }
      if (rows.length === 1) {
        handleCheckIn(rows[0]);
        return;
      }
      navigate(APP_ROUTES.checkInNewBulk, {
        state: {
          documents: rows.map((r) => ({ ...r })),
          from: APP_ROUTES.checkInNew,
        },
      });
    },
    [handleCheckIn, navigate],
  );

  const resolveAndNavigateCheckIn = useCallback(
    async (selectionItems) => {
      const selection = Array.isArray(selectionItems) ? selectionItems : [];
      if (!selection.length || checkInResolving) {
        return;
      }

      const hasFolders = selection.some((item) => item?.isFolder === true);
      const directFiles = selection.filter((item) => item?.isFolder !== true);

      if (!hasFolders) {
        navigateToCheckInForDocuments(directFiles);
        return;
      }

      setCheckInResolveError(null);
      setCheckInResolving(true);
      try {
        const userAuth = new UserAuth();
        const tokenResponse = await userAuth.getUserToken(true);
        const accessToken = tokenResponse?.accessToken ?? null;
        if (!accessToken) {
          setCheckInResolveError("No access token. Sign in again and retry.");
          return;
        }

        const { groupId, channelId, fileRoot } = resolveGroupId(teamsContext);
        const rows = await collectCheckInDocumentsFromSelection(selection, {
          accessToken,
          teamId: groupId,
          channelId,
          tenantId: teamsContext?.user?.tenant?.id ?? "",
          oneDriveRoot: fileRoot,
        });
        navigateToCheckInForDocuments(rows);
      } catch (err) {
        const msg =
          err?.response?.data?.message ??
          err?.message ??
          "Failed to load documents from the selected folder(s).";
        setCheckInResolveError(msg);
        logger.warn(
          "[CheckInNewListContent] folder expand for check-in failed",
          { msg },
          "DIALOG",
        );
      } finally {
        setCheckInResolving(false);
      }
    },
    [checkInResolving, navigateToCheckInForDocuments, teamsContext],
  );

  const handleFooterBulkCheckIn = useCallback(() => {
    void resolveAndNavigateCheckIn(getAllSelectedItems());
  }, [getAllSelectedItems, resolveAndNavigateCheckIn]);

  const handleFolderCheckIn = useCallback(
    (folderItem) => {
      const selected = getAllSelectedItems();
      if (selected.length === 0 && folderItem) {
        void resolveAndNavigateCheckIn([folderItem]);
        return;
      }
      void resolveAndNavigateCheckIn(selected);
    },
    [getAllSelectedItems, resolveAndNavigateCheckIn],
  );

  const openDoc = useCallback(
    (url) => openExternalUrl(resolveEditInBrowserUrl(url)),
    [],
  );

  const handleFolderOpen = useCallback(
    (item) => {
      if (!item?.key || !item?.name) return;

      // When opening a folder from search results the backend returns a folderPath array
      // with the full ancestor chain. Use it to build the correct breadcrumb path.
      // In normal (non-search) browsing folderPath is null; fall back to appending to the
      // current stack as before.
      if (Array.isArray(item.folderPath)) {
        const newStack = [...item.folderPath, { id: item.key, name: item.name }];
        setSearchValue("");
        setAppliedSearchValue("");
        setFolderStack(newStack);
        setCurrentPageIndex(0);
        fetchDocsAtFolder(item.key, 0, { searchValue: "" });
      } else {
        setFolderStack((prev) => [...prev, { id: item.key, name: item.name }]);
        setCurrentPageIndex(0);
        fetchDocsAtFolder(item.key, 0);
      }
    },
    [fetchDocsAtFolder]
  );

  const handleNavigateToRoot = useCallback(() => {
    setFolderStack([]);
    setCurrentPageIndex(0);
    fetchDocsAtFolder("", 0);
  }, [fetchDocsAtFolder]);

  const handleNavigateToFolderIndex = useCallback(
    (index) => {
      const nextStack = folderStack.slice(0, index + 1);
      const folderId =
        nextStack.length > 0 ? nextStack[nextStack.length - 1].id : "";
      setFolderStack(nextStack);
      setCurrentPageIndex(0);
      fetchDocsAtFolder(folderId, 0);
    },
    [folderStack, fetchDocsAtFolder]
  );

  const handleDeleteClick = useCallback((item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
    setDeleteError(null);
  }, []);

  const handleSharingDetailsClick = useCallback(
    (item) => {
      if (!item?.key) return;

      const { groupId, channelId, fileRoot } = resolveGroupId(teamsContext);
      const tenantId = teamsContext?.user?.tenant?.id ?? "";
      if (!tenantId) {
        logger.warn(
          "[CheckInNewListContent] sharing details skipped: no tenantId",
          null,
          "DIALOG",
        );
        return;
      }

      const fileName = item.name ?? "";
      const iconSrc = item.isFolder
        ? ""
        : item.iconName ||
          getFileIconPath(item.fileType || "", {
            FileExtension: fileName.includes(".")
              ? fileName.split(".").pop()
              : "",
            FileType: item.fileType || "",
          });

      openSharingDetails(fileName, iconSrc, item.isFolder === true);
      fetchSharingDetails(groupId, channelId, tenantId, item.key, fileRoot);
    },
    [teamsContext, openSharingDetails, fetchSharingDetails],
  );

  const handleStopSharingClick = useCallback(() => {
    setStopSharingConfirmOpen(true);
  }, []);

  const handleVersionHistoryClick = useCallback(
    async (item) => {
      if (!item?.key) return;

      const { groupId, channelId, fileRoot } = resolveGroupId(teamsContext);
      const tenantId = teamsContext?.user?.tenant?.id ?? "";
      if (!tenantId) {
        logger.warn(
          "[CheckInNewListContent] version history skipped: no tenantId",
          null,
          "DIALOG",
        );
        return;
      }

      setVersionHistoryDocument(item);
      setVersionHistoryOpen(true);
      setVersionHistoryLoading(true);
      setVersionHistoryError(null);
      setVersionHistoryData([]);

      try {
        const tokenResponse = await new UserAuth().getUserToken(true);
        const accessToken = tokenResponse?.accessToken ?? null;
        if (!accessToken) {
          setVersionHistoryError("Could not load version history for this document.");
          return;
        }
        const { data, status } = await getOneDriveVersionHistory(accessToken, {
          teamId: groupId,
          channelId,
          tenantId,
          documentId: item.key,
          oneDriveRoot: fileRoot ?? "",
        });
        if (status === 200 || status === 204) {
          setVersionHistoryData(Array.isArray(data) ? data : []);
        } else {
          setVersionHistoryError("Failed to load version history.");
        }
      } catch (error) {
        logger.error("Failed to load version history", error, "DIALOG");
        setVersionHistoryError(
          error?.response?.data?.message ??
            "Failed to load version history. Please try again.",
        );
      } finally {
        setVersionHistoryLoading(false);
      }
    },
    [teamsContext],
  );

  const handleCloseVersionHistory = useCallback(() => {
    setVersionHistoryOpen(false);
    setVersionHistoryData([]);
    setVersionHistoryError(null);
    setVersionHistoryDocument(null);
  }, []);

  const onConfirmStopSharing = useCallback(async () => {
    try {
      const result = await stopSharing();
      if (result?.meta?.requestStatus === "fulfilled") {
        dispatch({
          type: ON_SUCCESS_MESSAGE_INVOKE,
          payload: {
            type: SUCCESS_TYPE,
            message: "Sharing stopped successfully.",
          },
        });
      } else if (result?.meta?.requestStatus === "rejected") {
        dispatch({
          type: ON_ERROR_MESSAGE_INVOKE,
          payload: {
            type: ERROR_TYPE,
            message:
              result?.payload?.message ??
              "Could not stop sharing for this document.",
          },
        });
      }
    } catch (error) {
      logger.error("Failed to stop sharing", error);
      dispatch({
        type: ON_ERROR_MESSAGE_INVOKE,
        payload: {
          type: ERROR_TYPE,
          message: "Could not stop sharing for this document.",
        },
      });
    } finally {
      setStopSharingConfirmOpen(false);
    }
  }, [stopSharing, dispatch]);

  const onConfirmDelete = useCallback(async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);
    setDeleteError(null);

    const { groupId, fileRoot } = resolveGroupId(teamsContext);
    const effectiveGroupId = groupId || teamsContext?.team?.groupId || "";
    const membershipType = teamsContext?.channel?.membershipType || "Regular";
    const channelId = teamsContext?.channel?.id || "";

    try {
      await deleteOneDriveDocument(
        membershipType,
        itemToDelete.key,
        effectiveGroupId,
        null,
        DOC_CHECK_IN_TYPE.checkinNew,
        fileRoot,
        channelId,
      );

      dispatch({
        type: ON_SUCCESS_MESSAGE_INVOKE,
        payload: {
          type: SUCCESS_TYPE,
          message: `Document '${itemToDelete.name}' deleted successfully from OneDrive.`,
        },
      });

      setSelectedByKey((prev) => {
        const next = { ...prev };
        delete next[String(itemToDelete.key)];
        return next;
      });

      const paging = resolveOneDriveListFetchPaging({
        oneDriveFileType: oneDriveSelectedFileType,
        pageIndex: currentPageIndex,
        pageSize: PAGE_SIZE,
      });
      dispatchOneDriveFetch({
        ...fetchParams,
        recFrom: paging.recFrom,
        pageSize: paging.recSize,
      });
    } catch (error) {
      logger.error("Failed to delete document from OneDrive", error);
      setDeleteError(DELETE_FAILED_MESSAGE);
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  }, [itemToDelete, teamsContext, deleteOneDriveDocument, dispatch, dispatchOneDriveFetch, fetchParams, currentPageIndex, oneDriveSelectedFileType]);

  // Opens the Share dialog to collect the email(s) to grant access to.
  const handleShareClick = useCallback((item) => {
    setItemToShare(item);
    setShareError("");
    setShareDialogOpen(true);
  }, []);

  const handleShareDialogClose = useCallback(() => {
    if (isSharing) return;
    setShareDialogOpen(false);
    setItemToShare(null);
    setShareError("");
  }, [isSharing]);

  const runShare = useCallback(
    async ({ recipients, role, message, sendInvitation, busyLabel }) => {
      if (!itemToShare) return;
      const documentTitle = getTitle(
        itemToShare?.title || itemToShare?.name || "Document",
      );
      const emails = (recipients ?? []).map((r) => r.email);
      setIsSharing(true);
      setShareBusyLabel(busyLabel);
      setShareError("");
      try {
        const userAuth = new UserAuth();
        const tokenResponse = await userAuth.getUserToken(true);
        const accessToken = tokenResponse?.accessToken ?? null;
        if (!accessToken) {
          setShareError("No access token. Sign in again and retry.");
          return;
        }

        const shareLink = await createOneDriveShareLink(accessToken, itemToShare, {
          recipients,
          role,
          message,
          sendInvitation,
        });

        // Access was granted (invite or a new sharing link): flip the Sharing
        // column to "Shared" now instead of waiting for a list refresh.
        if (sendInvitation || isNewSharingLink(itemToShare, shareLink)) {
          dispatch(
            markOneDriveDocumentShared({
              documentId: itemToShare?.id ?? itemToShare?.Id,
            }),
          );
        }

        if (sendInvitation) {
          // "Send": access granted + invitation emailed. No clipboard step.
          setShareDialogOpen(false);
          setItemToShare(null);
          dispatch({
            type: ON_SUCCESS_MESSAGE_INVOKE,
            payload: {
              type: SUCCESS_TYPE,
              title: "Invitation sent",
              message: `${documentTitle} shared with ${emails.join(", ")}.`,
            },
          });
          return;
        }

        // "Copy link": grant (if recipients) + copy the link to the clipboard.
        if (!shareLink) {
          setShareError("Could not generate a sharing link for this document.");
          return;
        }
        // Keep the dialog open after copying; the toast confirms the copy.
        const copied = await copyTextToClipboard(shareLink);
        const scopeNote = emails.length
          ? ` Only ${emails.join(", ")} can open it.`
          : "";
        dispatch(
          copied
            ? {
                type: ON_SUCCESS_MESSAGE_INVOKE,
                payload: {
                  type: SUCCESS_TYPE,
                  title: "Link copied",
                  message: `Sharing link for ${documentTitle} copied to clipboard.${scopeNote}`,
                },
              }
            : {
                type: ON_ERROR_MESSAGE_INVOKE,
                payload: {
                  type: ERROR_TYPE,
                  message: emails.length
                    ? `Access was granted to ${emails.join(", ")}, but the link could not be copied to the clipboard.`
                    : "Sharing link generated but could not be copied to the clipboard.",
                },
              },
        );
      } catch (error) {
        logger.error("[CheckInNewListContent] share failed", error, "DIALOG");
        setShareError(
          error?.message || "Could not share this document. Please try again.",
        );
      } finally {
        setIsSharing(false);
        setShareBusyLabel("");
      }
    },
    [itemToShare, dispatch],
  );

  const handleShareCopyLink = useCallback(
    (recipients, role) => {
      void runShare({
        recipients,
        role,
        message: "",
        sendInvitation: false,
        busyLabel: "Generating link…",
      });
    },
    [runShare],
  );

  const handleShareSend = useCallback(
    (recipients, role, message) => {
      void runShare({
        recipients,
        role,
        message,
        sendInvitation: true,
        busyLabel: "Sending…",
      });
    },
    [runShare],
  );

  const getMenuProps = useCallback(
    (rowItem) => {
      const multiSelected = !rowItem?.isFolder && selectedCount > 1;
      const multiSelectHint =
        "Available when only one item is selected. Clear extra selections to use this action.";
      // Share works on a single item only (folders included).
      const shareDisabled = selectedCount > 1;
      // Share opens its options only while hovered (see HoverSubmenuMenuItem).
      const shareSubmenuItem = (disabled) => ({
        key: "shareMenu",
        text: "Share",
        onRender: (_item, dismissMenu) => (
          <HoverSubmenuMenuItem
            key="shareMenu"
            text="Share"
            items={[
              {
                key: "share",
                text: rowItem?.isFolder ? "Share Folder" : "Share Document",
                onClick: () => handleShareClick(rowItem),
              },
              {
                key: "sharingDetails",
                text: "Sharing Details",
                onClick: () => handleSharingDetailsClick(rowItem),
              },
            ]}
            disabled={disabled}
            title={disabled ? multiSelectHint : undefined}
            theme={theme}
            menuType={{
              fontFamily: theme.contextMenu?.fontFamily ?? theme.fonts.regular,
              fontSize:
                theme.contextMenu?.fontSize ?? theme.fontSizes?.menusize ?? 12,
            }}
            menuStyles={menuProps.styles}
            dismissMenu={dismissMenu}
          />
        ),
      });

      const menuItems = rowItem?.isFolder
        ? [
            {
              key: "checkIn",
              text: "Check-in",
              disabled: checkInResolving,
              onClick: () => {
                handleFolderCheckIn(rowItem);
              },
            },
            {
              key: "openFolder",
              text: "Open folder",
              onClick: () => handleFolderOpen(rowItem),
            },
            {
              key: "open",
              text: "Open in browser",
              onClick: () => openDoc(rowItem.weblink),
            },
            shareSubmenuItem(shareDisabled),
          ]
        : [
            {
              key: "checkIn",
              text: "Check-in",
              disabled: checkInResolving,
              onClick: () => {
                if (multiSelected) {
                  void handleFooterBulkCheckIn();
                  return;
                }
                handleCheckIn(rowItem);
              },
            },
            {
              key: "download",
              text: "Download to local",
              onClick: () => {
                if (multiSelected) {
                  void handleDownloadSelectedToLocal();
                  return;
                }

                handleDownload(rowItem, true);
              }
            },
            {
              key: "open",
              text: "Open in browser",
              disabled: multiSelected,
              title: multiSelected ? multiSelectHint : undefined,
              onClick: () => openDoc(rowItem.weblink),
            },
            {
              key: "openteams",
              text: "Preview in Teams",
              disabled: multiSelected,
              title: multiSelected ? multiSelectHint : undefined,
              onClick: () =>
                previewInTeams(
                  { key: rowItem.key },
                  APP_ROUTES.checkInNew,
                  {
                    [NAV_LOCATION_STATE.restoreCheckInNewFolder]: {
                      folderStack,
                      pageIndex: currentPageIndex,
                    },
                  }
                ),
            },
            shareSubmenuItem(shareDisabled),
            {
              key: "deleteOneDrive",
              text: "Delete from OneDrive",
              disabled: multiSelected,
              title: multiSelected ? multiSelectHint : undefined,
              onClick: () => handleDeleteClick(rowItem),
            },
            {
              key: "versionHistory",
              text: "OneDrive history",
              disabled: multiSelected,
              title: multiSelected ? multiSelectHint : undefined,
              onClick: () => handleVersionHistoryClick(rowItem),
            },
          ];
      // Named so the Share submenu can reuse these styles.
      const menuProps = {
        shouldFocusOnMount: true,
        items: menuItems,
        styles: {
          root: {
            border: `1px solid ${theme.colors.addovation20Green} !important`,
            fontFamily: theme.contextMenu?.fontFamily ?? theme.fonts.regular,
            fontSize: theme.contextMenu?.fontSize ?? theme.fontSizes?.menusize ?? 12,
            selectors: {
              ".ms-ContextualMenu-link:hover": {
                backgroundColor: theme.colors.contextMenuHover,
                color: theme.colors.fontColor,
              },
              ".ms-ContextualMenu-link.is-disabled": {
                backgroundColor: `${theme.colors.contextMenuBackground} !important`,
                color: `${theme.colors.deactiveColor} !important`,
                cursor: "not-allowed",
              },
              ".ms-ContextualMenu-link.is-disabled:hover, .ms-ContextualMenu-link.is-disabled:focus, .ms-ContextualMenu-link.is-disabled:active":
                {
                  backgroundColor: `${theme.colors.deactiveColorLight} !important`,
                  color: `${theme.colors.deactiveColor} !important`,
                  cursor: "not-allowed",
                },
              ".ms-ContextualMenu-link.is-disabled .ms-ContextualMenu-itemText":
                {
                  color: `${theme.colors.deactiveColor} !important`,
                },
              ".ms-ContextualMenu-itemText": {
                fontFamily: theme.contextMenu?.fontFamily ?? theme.fonts.regular,
                fontSize: theme.contextMenu?.fontSize ?? theme.fontSizes?.menusize ?? 12,
                color: theme.colors.fontColor,
              },
              ".ms-ContextualMenu-linkContent": {
                fontFamily: theme.contextMenu?.fontFamily ?? theme.fonts.regular,
                fontSize: theme.contextMenu?.fontSize ?? theme.fontSizes?.menusize ?? 12,
              },
              ".ms-FocusZone, .ms-ContextualMenu, .is-open, .ms-BaseButton-menuhost": {
                backgroundColor: theme.colors.contextMenuBackground,
              },
            },
          },
        },
      };
      return menuProps;
    },
    [
      theme,
      openDoc,
      previewInTeams,
      handleDownload,
      handleDownloadSelectedToLocal,
      handleCheckIn,
      handleFooterBulkCheckIn,
      handleFolderCheckIn,
      handleFolderOpen,
      handleDeleteClick,
      handleSharingDetailsClick,
      handleVersionHistoryClick,
      handleShareClick,
      folderStack,
      currentPageIndex,
      selectedCount,
      checkInResolving,
    ],
  );

  const handleSearch = useCallback(() => {
    const term = (searchValue ?? "").trim();
    
    setFolderStack([]);
    setCurrentPageIndex(0);
    setAppliedSearchValue(term);
    const paging = resolveOneDriveListFetchPaging({
      oneDriveFileType: oneDriveSelectedFileType,
      pageIndex: 0,
      pageSize: PAGE_SIZE,
    });
    dispatchOneDriveFetch({
      ...buildFetchParams({
        teamsContext,
        searchValue: term,
        oneDriveSelectedFileType,
        pageSize: PAGE_SIZE,
        folderId: "",
        dateRange: activeDateRange,
      }),
      recFrom: paging.recFrom,
      pageSize: paging.recSize,
    });
  }, [
    dispatchOneDriveFetch,
    teamsContext,
    searchValue,
    oneDriveSelectedFileType,
    activeDateRange,
  ]);

  const handleClearAndSearchAll = useCallback(() => {
    setSearchValue("");
    setAppliedSearchValue("");
    setCurrentPageIndex(0);
    const paging = resolveOneDriveListFetchPaging({
      oneDriveFileType: oneDriveSelectedFileType,
      pageIndex: 0,
      pageSize: PAGE_SIZE,
    });
    dispatchOneDriveFetch({
      ...buildFetchParams({
        teamsContext,
        searchValue: "",
        oneDriveSelectedFileType,
        pageSize: PAGE_SIZE,
        folderId: currentFolderId,
        dateRange: activeDateRange,
      }),
      recFrom: paging.recFrom,
      pageSize: paging.recSize,
    });
  }, [
    dispatchOneDriveFetch,
    teamsContext,
    oneDriveSelectedFileType,
    currentFolderId,
    activeDateRange,
  ]);

  const handleFileTypeChange = useCallback(
    (fileType) => {
      setOneDriveSelectedFileType(fileType);
      setCurrentPageIndex(0);
      const effectiveSearch = resolveEffectiveSearchValue(
        appliedSearchValue,
        searchValue,
      );
      const paging = resolveOneDriveListFetchPaging({
        oneDriveFileType: fileType,
        pageIndex: 0,
        pageSize: PAGE_SIZE,
      });
      dispatchOneDriveFetch({
        ...buildFetchParams({
          teamsContext,
          searchValue: effectiveSearch,
          oneDriveSelectedFileType: fileType,
          pageSize: PAGE_SIZE,
          folderId: currentFolderId,
          dateRange: activeDateRange,
        }),
        recFrom: paging.recFrom,
        pageSize: paging.recSize,
      });
    },
    [
      dispatchOneDriveFetch,
      teamsContext,
      appliedSearchValue,
      searchValue,
      currentFolderId,
      activeDateRange,
    ],
  );

  const handleDateFilterEnabledChange = useCallback(
    (enabled) => {
      setIsDateFilterEnabled(enabled);
      setCurrentPageIndex(0);

      const range = appliedDateRange ?? dateRange ?? getDefaultDateRange();
      if (enabled) {
        setAppliedDateRange(range);
        setDateRange(range);
      }

      const effectiveSearch = resolveEffectiveSearchValue(
        appliedSearchValue,
        searchValue,
      );
      const paging = resolveOneDriveListFetchPaging({
        oneDriveFileType: oneDriveSelectedFileType,
        pageIndex: 0,
        pageSize: PAGE_SIZE,
      });
      dispatchOneDriveFetch({
        ...buildFetchParams({
          teamsContext,
          searchValue: effectiveSearch,
          oneDriveSelectedFileType,
          pageSize: PAGE_SIZE,
          folderId: currentFolderId,
          dateRange: enabled ? range : null,
        }),
        recFrom: paging.recFrom,
        pageSize: paging.recSize,
      });
    },
    [
      dispatchOneDriveFetch,
      teamsContext,
      appliedSearchValue,
      searchValue,
      oneDriveSelectedFileType,
      currentFolderId,
      appliedDateRange,
      dateRange,
    ],
  );

  const handleDateRangeApply = useCallback(
    (nextRange) => {
      setIsDateFilterEnabled(true);
      setDateRange(nextRange);
      setAppliedDateRange(nextRange);
      setCurrentPageIndex(0);
      const effectiveSearch = resolveEffectiveSearchValue(
        appliedSearchValue,
        searchValue,
      );
      const paging = resolveOneDriveListFetchPaging({
        oneDriveFileType: oneDriveSelectedFileType,
        pageIndex: 0,
        pageSize: PAGE_SIZE,
      });
      dispatchOneDriveFetch({
        ...buildFetchParams({
          teamsContext,
          searchValue: effectiveSearch,
          oneDriveSelectedFileType,
          pageSize: PAGE_SIZE,
          folderId: currentFolderId,
          dateRange: nextRange,
        }),
        recFrom: paging.recFrom,
        pageSize: paging.recSize,
      });
    },
    [
      dispatchOneDriveFetch,
      teamsContext,
      appliedSearchValue,
      searchValue,
      oneDriveSelectedFileType,
      currentFolderId,
    ],
  );

  const handleSelectionSummaryClick = useCallback(() => {
    if (selectedCount === 0) return;
    setShowSelectedOnly((prev) => {
      if (!prev) {
        setCurrentPageIndex(0);
      }
      return !prev;
    });
  }, [selectedCount]);

  const errorMessage = message || SEARCH_RESULT_FAILED;
  const canLoadList = Boolean(teamsContext?.user?.tenant?.id) && isPanelActive;
  const isAwaitingInitialLoad = canLoadList && !initialized && !isError;
  const showContentLoader =
    canLoadList && (loading || isAwaitingInitialLoad) && !showSelectedOnly;
  const showNoDocumentsState =
    initialized &&
    !loading &&
    !isError &&
    !showSelectedOnly &&
    tableItems.length === 0;
  const showTable =
    initialized &&
    !loading &&
    !isError &&
    (showSelectedOnly ? selectedCount > 0 : tableItems.length > 0);
  const selectionAccentColor =
    theme.colors.themeDarkColor ?? theme.colors.addovation20Green ?? "#249f5d";

  const listFooterBar = (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
        alignItems: "center",
        columnGap: 8,
        width: "100%",
        boxSizing: "border-box",
        padding: "10px 12px",
        background: theme.colors.backgroundColor,
        borderTop: `1px solid ${theme.colors.addovation10Grey}`,
      }}
    >
      <div
        style={{
          justifySelf: "start",
          minHeight: 20,
        }}
        aria-live="polite"
      >
        {selectedCount > 0 ? (
          <button
            type="button"
            onClick={handleSelectionSummaryClick}
            aria-pressed={showSelectedOnly}
            style={{
              color: selectionAccentColor,
              fontFamily: theme.fonts.regular,
              fontSize: theme.fontSizes.normal,
              fontWeight: theme.fontWeight.semibold ?? 600,
              cursor: "pointer",
              background: "none",
              border: "none",
              padding: 0,
              textAlign: "left",
              textDecoration: showSelectedOnly ? "underline" : "none",
            }}
          >
            {showSelectedOnly
              ? `Showing ${selectedCount} selected · Show all`
              : `${selectedCount} item${selectedCount === 1 ? "" : "s"} selected`}
          </button>
        ) : null}
      </div>
      <div
        style={{
          justifySelf: "center",
        }}
      >
        <DocumentsPagination
          navigationLoading={showSelectedOnly ? false : loading}
          pageSize={PAGE_SIZE}
          documents={
            showSelectedOnly
              ? displayTableItems
              : useClientSideFileTypePaging
                ? pagedDocs
                : (docs ?? [])
          }
          hasNext={
            showSelectedOnly
              ? (currentPageIndex + 1) * PAGE_SIZE < allSelectedItems.length
              : useClientSideFileTypePaging
                ? (currentPageIndex + 1) * PAGE_SIZE < filteredDocs.length
                : undefined
          }
          selectedPageIndex={currentPageIndex}
          onPageIndexChange={handlePageChange}
        />
      </div>
      <div aria-hidden />
    </div>
  );

  return (
    <Stack
      enableScopedSelectors
      styles={{
        root: {
          width: "100%",
          height: "100%",
          flex: 1,
          minHeight: 0,
          backgroundColor: theme.colors.backgroundColor,
        },
      }}
      tokens={{ childrenGap: 0, padding: 0 }}
    >
      <Stack.Item
        disableShrink
        grow
        styles={{
          root: {
            backgroundColor: theme.colors.backgroundColor,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
            height: "100%",
          },
        }}
      >
        <Stack.Item shrink={0} styles={{ root: { flexShrink: 0 } }}>
          <TeamsSearchBar
            searchValue={searchValue}
            onSearchValueChange={setSearchValue}
            onSearch={handleSearch}
            onClearAndSearch={handleClearAndSearchAll}
            searchLoading={loading || isAwaitingInitialLoad}
            placeholder={"Search for documents"}
            selectedFileType={oneDriveSelectedFileType}
            onFileTypeChange={handleFileTypeChange}
            isFilterVisible={isOneDriveFilterVisible}
            onFilterVisibleChange={setIsOneDriveFilterVisible}
            onBrowseFiles={handleBrowseFiles}
            onBrowseFolder={handleBrowseFolder}
            browseDisabled={!fileTypesReady}
            dateFilterEnabled={isDateFilterEnabled}
            dateRange={dateRange}
            onDateRangeApply={handleDateRangeApply}
            onDateFilterEnabledChange={handleDateFilterEnabledChange}
          />
        </Stack.Item>
        {folderStack.length > 0 && (
          <CheckInNewFolderBreadcrumb
            folderStack={folderStack}
            rootLabel={channelDocsLabel}
            theme={theme}
            onNavigateToRoot={handleNavigateToRoot}
            onNavigateToIndex={handleNavigateToFolderIndex}
          />
        )}
        {showContentLoader ? (
          <Stack
            horizontalAlign="center"
            verticalAlign="center"
            styles={{
              root: {
                width: "100%",
                padding: "24px 0",
                flexShrink: 0,
                background: theme.colors.backgroundColor,
              },
            }}
          >
            <Loader />
          </Stack>
        ) : isError ? (
          <CheckInNewListErrorDisplay message={errorMessage} />
        ) : showNoDocumentsState ? (
          <Stack
            enableScopedSelectors
            grow
            verticalFill
            styles={{
              root: {
                background: theme.colors.backgroundColor,
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            <Stack.Item grow styles={{ root: { flex: 1, minHeight: 0 } }}>
              <CheckInNewListNoDataDisplay
                message={
                  folderStack.length > 0
                    ? "This folder is empty."
                    : "No documents found"
                }
              />
            </Stack.Item>
            {selectedCount > 0 ? (
              <Stack.Item shrink={0}>{listFooterBar}</Stack.Item>
            ) : null}
          </Stack>
        ) : showTable ? (
          <Stack
            enableScopedSelectors
            grow
            verticalFill
            style={gridThemeStyle.borderStyle}
            styles={{
              root: {
                background: theme.colors.backgroundColor,
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            <CheckInNewDocumentsTable
              items={displayTableItems}
              selectedByKey={selectedByKey}
              onToggleRowSelection={handleToggleRowSelection}
              onToggleAllVisible={handleToggleAllVisible}
              onSelectRowForMenu={handleSelectRowForMenu}
              theme={theme}
              gridThemeStyle={gridThemeStyle}
              getMenuProps={getMenuProps}
              onFolderClick={handleFolderOpen}
              onOpenInIFS={openInIFS}
              onSharingClick={handleSharingDetailsClick}
            />
            <Stack.Item shrink={0}>{listFooterBar}</Stack.Item>
          </Stack>
        ) : null}
      </Stack.Item>
      <ReusableMessageDialog
        isOpen={previewIsOpen}
        onClose={closePreviewDialog}
        title={previewIsError ? "Preview failed" : "Preview in Teams"}
        showHeader={previewIsError}
        hideHeaderDivider={previewIsError}
        message={previewIsError ? "Could not open the document preview." : previewMessage}
        errorMessage={previewErrorMessage}
        showDetailsButton={previewIsError && !!previewErrorMessage}
        showOkButton={previewIsError}
        variant={previewIsError ? "error" : "normal"}
        isLoading={previewLoading}
        loadingText={previewLoadingMessage || "Opening preview…"}
        dialogMinHeight={60}
        bodyMinHeight={50}
      />
      <ReusableMessageDialog
        key={`download-${showDownloadDialog}-${downloadLoading}-${bulkDownloading}`}
        isOpen={showDownloadDialog}
        onClose={handleDownloadDialogClose}
        title={downloadIsError ? "Download failed" : "Download"}
        showHeader={downloadIsError}
        message={
          downloadIsError
            ? "Could not prepare the file for download."
            : downloadMessage
        }
        errorMessage={downloadMessage}
        showDetailsButton={downloadIsError && !!downloadMessage}
        showOkButton={downloadIsError}
        variant={downloadIsError ? "error" : "normal"}
        isLoading={downloadLoading || bulkDownloading}
        loadingText="Downloading..."
        dialogMinHeight={60}
        bodyMinHeight={50}
        isBlocking={downloadLoading || bulkDownloading}
      />
      <ReusableMessageDialog
        isOpen={checkInResolving || !!checkInResolveError}
        onClose={() => setCheckInResolveError(null)}
        title={
          checkInResolving
            ? "Check-in"
            : checkInResolveError === NO_DOCS_IN_FOLDER_MESSAGE
              ? "No documents found"
              : "Check-in failed"
        }
        showHeader={!!checkInResolveError}
        message={
          checkInResolveError === NO_DOCS_IN_FOLDER_MESSAGE
            ? NO_DOCS_IN_FOLDER_MESSAGE
            : checkInResolveError
              ? "Could not prepare documents from the selected folder(s)."
              : ""
        }
        errorMessage={
          checkInResolveError === NO_DOCS_IN_FOLDER_MESSAGE
            ? ""
            : (checkInResolveError ?? "")
        }
        showDetailsButton={
          !!checkInResolveError &&
          checkInResolveError !== NO_DOCS_IN_FOLDER_MESSAGE
        }
        showOkButton={!!checkInResolveError}
        variant={
          checkInResolveError === NO_DOCS_IN_FOLDER_MESSAGE
            ? "normal"
            : checkInResolveError
              ? "error"
              : "normal"
        }
        isLoading={checkInResolving}
        loadingText="Loading documents from folder(s)…"
        dialogMinHeight={60}
        bodyMinHeight={50}
        isBlocking={checkInResolving}
      />
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
        theme={theme}
        type="delete"
        title={DELETE_DOCUMENT_TITLE}
        subText={
          deleteError
            ? deleteError
            : itemToDelete
              ? `Are you sure you want to delete "${getTitle(itemToDelete?.name || itemToDelete?.title || "this document")}" from OneDrive?`
              : DELETE_DOCUMENT_SUB_TITLE
        }
        isDeleting={isDeleting}
      />
      <ShareDocumentDialog
        isOpen={shareDialogOpen}
        onClose={handleShareDialogClose}
        onCopyLink={handleShareCopyLink}
        onSend={handleShareSend}
        documentTitle={itemToShare?.title || itemToShare?.name || ""}
        isBusy={isSharing}
        busyLabel={shareBusyLabel}
        errorMessage={shareError}
        theme={theme}
      />
      <ConfirmModal
        isOpen={stopSharingConfirmOpen}
        onConfirm={onConfirmStopSharing}
        onCancel={() => setStopSharingConfirmOpen(false)}
        theme={theme}
        type="delete"
        title="Stop sharing?"
        subText={`This will remove sharing links and access for people and groups (except the owner) from "${getTitle(sharingDetailsState.documentName || "this document")}".`}
        confirmButtonText="Stop sharing"
        isDeleting={sharingDetailsState.stopping}
      />
      <SharingDetailsDialog
        isOpen={sharingDetailsState.isOpen}
        onClose={closeSharingDetails}
        loading={sharingDetailsState.loading}
        stopping={sharingDetailsState.stopping}
        isError={sharingDetailsState.isError}
        message={sharingDetailsState.message}
        documentName={sharingDetailsState.documentName}
        iconSrc={sharingDetailsState.iconSrc}
        isFolder={sharingDetailsState.isFolder}
        details={sharingDetailsState.details}
        // Stop sharing is only offered in the General add-in, not in channels.
        onStopSharing={isGeneralAppContext ? handleStopSharingClick : undefined}
        onRemoveLink={(link) => removeSharingLink({ permissionId: link.id })}
        onRemoveLinkRecipient={(link, person) =>
          removeSharingLink({
            permissionId: link.id,
            recipientEmail: person.email || undefined,
            recipientId: person.email ? undefined : person.id,
          })
        }
        removingLinkKey={sharingDetailsState.removingLinkKey}
        linkError={sharingDetailsState.linkError}
        onChangePersonRole={(person, role) =>
          updateSharingRole({
            recipientEmail: person.email || undefined,
            recipientId: person.email ? undefined : person.id,
            role,
          })
        }
        updatingPersonKey={sharingDetailsState.updatingPersonKey}
      />
      <OneDriveVersionHistoryDialog
        isOpen={versionHistoryOpen}
        onClose={handleCloseVersionHistory}
        historyData={versionHistoryData}
        isLoading={versionHistoryLoading}
        error={versionHistoryError}
        document={versionHistoryDocument}
      />
    </Stack>
  );
}
